import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import { trackEvent } from '../utils/analytics';

const { FiChevronDown, FiZap, FiBarChart2, FiMessageSquare, FiGlobe, FiCpu, FiMapPin, FiDollarSign, FiHelpCircle, FiLayout, FiMenu, FiX, FiArrowRight, FiMail, FiCompass, FiAward, FiPercent, FiCode, FiHeart, FiMap, FiMusic, FiBookOpen, FiUsers, FiFeather, FiGrid, FiLayers, FiTag, FiTrendingUp, FiShare2, FiShield, FiCreditCard, FiVideo, FiFacebook, FiUserCheck, FiRadio, FiVolume2, FiCamera, FiUser } = FiIcons;

const MobileAccordion = ({ group, setMobileMenuOpen, handleNavClick, currentLang, getHref }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();

  return (
    <div className="border-b border-slate-50 last:border-0">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center py-4 text-slate-900 font-bold"
      >
        {group.label}
        <SafeIcon icon={FiChevronDown} className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pb-4 space-y-4">
              {group.id === 'platform' ? (
                <>
                  {/* Platform Sub-Section */}
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest pl-2 pt-1">
                      {t('navbar.groups.platform')}
                    </div>
                    <div className="space-y-2">
                      {group.items.map((item) => (
                        <a
                          key={item.name}
                          href={item.href}
                          onClick={(e) => handleNavClick(e, item.href, item.onClick)}
                          className="flex items-center gap-3 p-3 rounded-xl bg-slate-50/50 text-slate-600"
                        >
                          <SafeIcon icon={item.icon} className="w-5 h-5 text-indigo-500" />
                          <span className="text-sm font-semibold">{item.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Event Creators Sub-Section */}
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest pl-2 pt-1">
                      {t('navbar.eventCreators.title')}
                    </div>
                    <div className="space-y-2">
                      {[
                        { name: t('navbar.eventCreators.firstTimers'), href: 'https://app.bookmeinscotty.com/add-item', icon: FiCompass },
                        { name: t('navbar.eventCreators.eventPros'), href: `/${currentLang}?page=solutions&tab=pilot`, icon: FiAward },
                        { name: t('navbar.eventCreators.freeEvents'), href: getHref('#pricing'), icon: FiPercent },
                        { name: t('navbar.eventCreators.developers'), href: 'https://docs.bookmein.events/', icon: FiCode },
                      ].map((item) => (
                        <a
                          key={item.name}
                          href={item.href}
                          onClick={(e) => handleNavClick(e, item.href)}
                          className="flex items-center gap-3 p-3 rounded-xl bg-slate-50/50 text-slate-600"
                        >
                          <SafeIcon icon={item.icon} className="w-5 h-5 text-indigo-500" />
                          <span className="text-sm font-semibold">{item.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Industry solutions Sub-Section */}
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest pl-2 pt-1">
                      {t('navbar.industrySolutions.title')}
                    </div>
                    <div className="space-y-2">
                      {[
                        { name: t('navbar.industrySolutions.festivals'), href: `/${currentLang}?page=solutions&tab=pilot`, icon: FiMusic },
                        { name: t('navbar.industrySolutions.workshops'), href: `/${currentLang}?page=solutions&tab=unity`, icon: FiBookOpen },
                        { name: t('navbar.industrySolutions.conferences'), href: `/${currentLang}?page=solutions&tab=pollio`, icon: FiUsers },
                        { name: t('navbar.industrySolutions.agritourism'), href: getHref('#formats'), icon: FiFeather },
                        { name: t('navbar.industrySolutions.charities'), href: getHref('#pricing'), icon: FiHeart },
                      ].map((item) => (
                        <a
                          key={item.name}
                          href={item.href}
                          onClick={(e) => handleNavClick(e, item.href)}
                          className="flex items-center gap-3 p-3 rounded-xl bg-slate-50/50 text-slate-600"
                        >
                          <SafeIcon icon={item.icon} className="w-5 h-5 text-indigo-500" />
                          <span className="text-sm font-semibold">{item.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  {group.items.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      onClick={(e) => handleNavClick(e, item.href, item.onClick)}
                      className="flex items-center gap-3 p-3 rounded-xl bg-slate-50/50 text-slate-600"
                    >
                      <SafeIcon icon={item.icon} className="w-5 h-5 text-indigo-500" />
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{item.name}</span>
                        {item.isNew && (
                          <span className="text-[9px] font-black uppercase text-white bg-amber-500 px-1.5 py-0.2 rounded-md">
                            NEW
                          </span>
                        )}
                      </div>
                    </a>
                  ))}
                  {group.id === 'resources' && (
                    <a
                      href="https://blog.bookmein.events"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        if (setMobileMenuOpen) {
                          setMobileMenuOpen(false);
                        }
                      }}
                      className="flex items-center gap-3 p-3 rounded-xl bg-indigo-50/40 border border-indigo-100/50 text-slate-600 hover:bg-indigo-50/60"
                    >
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
                        <SafeIcon icon={FiFeather} className="w-4 h-4" />
                      </div>
                      <div className="flex-1 flex items-center justify-between">
                        <span className="text-sm font-bold text-slate-900">{t('navbar.blogPromo.title')}</span>
                        <span className="text-[9px] font-black uppercase tracking-widest text-indigo-600 bg-indigo-100/50 px-1.5 py-0.5 rounded border border-indigo-200/50">
                          Blog
                        </span>
                      </div>
                    </a>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LanguageSelector = ({ dropUp = false }) => {
  const { currentLang, changeLang } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: 'en', name: 'EN', label: 'English' },
    { code: 'gr', name: 'GR', label: 'Ελληνικά' },
    { code: 'de', name: 'DE', label: 'Deutsch' },
    { code: 'es', name: 'ES', label: 'Español' },
    { code: 'fr', name: 'FR', label: 'Français' }
  ];

  const current = languages.find(l => l.code === currentLang) || languages[0];

  return (
    <div 
      className="relative"
      onMouseEnter={() => !dropUp && setIsOpen(true)}
      onMouseLeave={() => !dropUp && setIsOpen(false)}
    >
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white/50 backdrop-blur-md text-slate-700 text-xs font-bold hover:bg-slate-100 hover:text-indigo-600 transition-all shadow-sm"
      >
        <SafeIcon icon={FiGlobe} className="w-3.5 h-3.5 text-indigo-500" />
        {current.name}
        <SafeIcon icon={FiChevronDown} className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: dropUp ? -5 : 5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: dropUp ? -5 : 5, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute right-0 ${dropUp ? 'bottom-full mb-2 origin-bottom-right' : 'top-full mt-2 origin-top-right'} w-32 z-50`}
          >
            <div className="bg-white rounded-xl shadow-xl border border-slate-100 p-1.5 overflow-hidden">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    changeLang(lang.code);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-xs font-bold transition-all ${
                    currentLang === lang.code 
                      ? 'bg-indigo-50 text-indigo-600' 
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <span>{lang.label}</span>
                  {currentLang === lang.code && <span className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Navbar = () => {
  const { t, currentLang, setIsContactModalOpen } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const pageParam = searchParams.get('page') || searchParams.get('pages');
  const isHomePage = ['/en', '/gr', '/de', '/es', '/fr', '/', ''].includes(location.pathname.replace(/\/$/, '').toLowerCase()) && !pageParam;

  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMega, setActiveMega] = useState(null);
  const headerRef = useRef(null);

  const getHref = (href) => {
    if (!href) return '#';
    if (href.startsWith('#') && !isHomePage) {
      return `/${currentLang}${href}`;
    }
    return href;
  };

  const handleNavClick = (e, href, onClick) => {
    if (onClick) {
      e.preventDefault();
      onClick(e);
      setActiveMega(null);
      setMobileMenuOpen(false);
      return;
    }
    if (!href || href === '#') {
      e.preventDefault();
      return;
    }
    // External link
    if (href.startsWith('http://') || href.startsWith('https://')) {
      setActiveMega(null);
      setMobileMenuOpen(false);
      return;
    }

    // Internal link
    e.preventDefault();
    setActiveMega(null);
    setMobileMenuOpen(false);

    if (href.startsWith('#')) {
      if (isHomePage) {
        const id = href.replace('#', '');
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
        window.history.pushState(null, '', href);
      } else {
        navigate(`/${currentLang}${href}`);
      }
      return;
    }

    if (href.includes('#')) {
      navigate(href);
      const hash = href.split('#')[1];
      if (hash) {
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
          }
        }, 120);
      }
      return;
    }

    navigate(href);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const navGroups = [
    {
      id: 'platform',
      label: t('navbar.groups.platform'),
      items: [
        { name: t('navbar.productItems.ticketing'), href: getHref('#features'), icon: FiTag, desc: t('navbar.productItems.ticketingDesc') },
        { name: t('navbar.productItems.aiMarketing'), href: getHref('#analytics'), icon: FiTrendingUp, desc: t('navbar.productItems.aiMarketingDesc') },
        { name: t('navbar.productItems.socialMedia'), href: getHref('#feedback'), icon: FiShare2, desc: t('navbar.productItems.socialMediaDesc') },
        { name: t('navbar.productItems.whitelabeling'), href: getHref('#branding'), icon: FiShield, desc: t('navbar.productItems.whitelabelingDesc') },
        { name: t('navbar.productItems.fairFees'), href: getHref('#pricing'), icon: FiPercent, desc: t('navbar.productItems.fairFeesDesc') },
        { name: t('navbar.productItems.realSupport'), href: getHref('#faq'), icon: FiHelpCircle, desc: t('navbar.productItems.realSupportDesc') },
        { name: t('navbar.productItems.payouts'), href: getHref('#partners'), icon: FiCreditCard, desc: t('navbar.productItems.payoutsDesc') },
        { name: t('navbar.productItems.allInOne'), href: getHref('#formats'), icon: FiGlobe, desc: t('navbar.productItems.allInOneDesc') },
      ]
    },
    {
      id: 'solutions',
      label: t('navbar.groups.solutions'),
      href: `/${currentLang}?page=solutions&tab=ecosystem`,
      items: [
        { id: 'pilot', name: 'Pilot', href: `/${currentLang}?page=solutions&tab=pilot`, icon: FiRadio, desc: 'Central Command for Logistics' },
        { id: 'unity', name: 'Unity', href: `/${currentLang}?page=solutions&tab=unity`, icon: FiGlobe, desc: 'Conversion-First Event Embedding' },
        { id: 'scotty', name: 'Scotty', href: `/${currentLang}?page=solutions&tab=scotty`, icon: FiVolume2, desc: '24/7 AI Guest Concierge' },
        { id: 'pollio', name: 'Pollio (NEW)', href: `/${currentLang}?page=solutions&tab=pollio`, icon: FiBarChart2, desc: 'Real-Time Audience Engagement', isNew: true },
        { id: 'ecosystem', name: 'Ecosystem', href: `/${currentLang}?page=solutions&tab=ecosystem`, icon: FiLayers, desc: 'The Event Lifecycle of Success' },
        { id: 'memories', name: 'Memories', href: `/${currentLang}?page=solutions&tab=memories`, icon: FiCamera, desc: 'Private AI Social Galleries' },
        { id: 'avatars', name: 'Avatars', href: `/${currentLang}?page=solutions&tab=avatars`, icon: FiUser, desc: 'Immersive Virtual Venues' },
        { id: 'bifrost', name: 'Bifrost', href: `/${currentLang}?page=solutions&tab=bifrost`, icon: FiShare2, desc: '13-Network Social Distribution' },
        { id: 'nima', name: 'Nima', href: `/${currentLang}?page=solutions&tab=nima`, icon: FiUsers, desc: 'Automate 1,000+ App Workflows' },
      ]
    },
    {
      id: 'resources',
      label: t('navbar.groups.resources'),
      items: [
        { name: t('navbar.items.ecosystem'), href: `/${currentLang}?page=solutions&tab=ecosystem`, icon: FiLayers, desc: t('navbar.items.ecosystemDesc') },
        { name: t('navbar.items.calculator'), href: getHref('#calculator'), icon: FiZap, desc: t('navbar.items.calculatorDesc') },
        { name: t('navbar.items.support'), href: getHref('#faq'), icon: FiHelpCircle, desc: t('navbar.items.supportDesc') },
        {
          name: t('navbar.groups.contact'),
          href: '#',
          icon: FiMail,
          desc: t('navbar.items.contactDesc'),
          onClick: () => {
            setIsContactModalOpen(true);
            setMobileMenuOpen(false);
          }
        },
      ]
    },
    {
      id: 'pricing',
      label: t('navbar.items.pricing'),
      href: getHref('#pricing'),
      items: []
    },
    {
      id: 'integrations',
      label: t('navbar.items.integrations'),
      href: getHref('#partners'),
      items: [
        { name: t('navbar.integrationItems.videoStreaming'), href: getHref('#formats'), icon: FiVideo, desc: t('navbar.integrationItems.videoStreamingDesc') },
        { name: t('navbar.integrationItems.marketingViral'), href: getHref('#feedback'), icon: FiFacebook, desc: t('navbar.integrationItems.marketingViralDesc') },
        { name: t('navbar.integrationItems.crmEmailRevenue'), href: getHref('#partners'), icon: FiUserCheck, desc: t('navbar.integrationItems.crmEmailRevenueDesc') },
      ]
    }
  ];

  return (
    <>
      <header 
        ref={headerRef}
        onMouseLeave={() => setActiveMega(null)}
        className={`fixed top-0 w-full z-[9999] transition-all duration-300 ${scrolled ? 'glass-nav py-3 shadow-sm' : 'bg-transparent py-5'}`}
      >
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <a 
              href={`/${currentLang}`} 
              onClick={(e) => handleNavClick(e, `/${currentLang}`)}
              className="flex items-center gap-2 cursor-pointer" 
              onMouseEnter={() => setActiveMega(null)}
            >
              <img src="/logo.png" alt="BookMeIn.Events Logo" className="h-8 w-auto object-contain" />
            </a>

            <nav className="hidden md:flex items-center gap-10">
              {navGroups.map((group) => {
                const isInteractive = group.id === 'platform' || group.id === 'solutions' || group.id === 'resources' || group.id === 'integrations';
                if (isInteractive) {
                  return (
                    <button
                      key={group.id}
                      onClick={(e) => {
                        if (group.href) {
                          handleNavClick(e, group.href);
                        }
                      }}
                      onMouseEnter={() => setActiveMega(group.id)}
                      className="flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-indigo-600 transition-colors py-2 cursor-pointer"
                    >
                      {group.label}
                      <SafeIcon icon={FiChevronDown} className={`w-3.5 h-3.5 transition-transform duration-300 ${activeMega === group.id ? 'rotate-180' : ''}`} />
                    </button>
                  );
                } else {
                  return (
                    <a
                      key={group.id}
                      href={group.href || '#'}
                      onClick={(e) => handleNavClick(e, group.href)}
                      onMouseEnter={() => setActiveMega(null)}
                      className="text-sm font-semibold text-slate-600 hover:text-indigo-600 transition-colors py-2 cursor-pointer"
                    >
                      {group.label}
                    </a>
                  );
                }
              })}
            </nav>

            <div className="hidden md:flex items-center gap-3" onMouseEnter={() => setActiveMega(null)}>
              <a 
                href="https://app.bookmeinscotty.com/"
                onClick={() => trackEvent('cta_click', { cta: 'login', section: 'navbar_desktop' })}
                className="px-4 py-1.5 rounded-full border border-slate-300/80 bg-white/50 backdrop-blur-md text-xs font-bold text-slate-700 hover:border-indigo-600 hover:text-indigo-600 hover:bg-white transition-all shadow-sm text-center flex items-center justify-center"
              >
                {t('navbar.login')}
              </a>
              <a 
                href="https://app.bookmeinscotty.com/add-item"
                onClick={() => trackEvent('cta_click', { cta: 'create_free', section: 'navbar_desktop' })}
                className="bg-gradient-primary text-white px-6 py-2.5 rounded-full text-sm font-bold hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300 transform hover:-translate-y-0.5 text-center flex items-center justify-center"
              >
                {t('navbar.createFree')}
              </a>
              <LanguageSelector />
            </div>

            <button className="md:hidden text-slate-600 p-1.5 -mr-1" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Separate Glassmorphic dropdown cards matching page width */}
        <AnimatePresence>
          {activeMega && (
            <motion.div
              initial={{ opacity: 0, y: -10, x: "-50%" }}
              animate={{ opacity: 1, y: 0, x: "-50%" }}
              exit={{ opacity: 0, y: -10, x: "-50%" }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="absolute top-full pt-3 left-1/2 w-[calc(100%-2rem)] sm:w-[calc(100%-3rem)] lg:w-[calc(100%-4rem)] max-w-[1440px] z-40 hidden md:block"
              onMouseEnter={() => setActiveMega(activeMega)}
              onMouseLeave={() => setActiveMega(null)}
            >
              <div className={`bg-white/95 backdrop-blur-xl border border-white/50 shadow-[0_20px_50px_rgba(0,0,0,0.12)] rounded-3xl inner-glow px-8 py-10 grid gap-8 relative overflow-hidden text-left ${activeMega === 'platform' || activeMega === 'solutions' ? 'grid-cols-4' : 'grid-cols-3'}`}>
                {activeMega === 'platform' && (
                  <>
                    {/* Column 1: Product */}
                    <div className="space-y-4 col-span-2">
                      <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-widest border-b border-indigo-50 pb-2">
                        {t('navbar.groups.platform')}
                      </h3>
                      <div className="grid grid-cols-2 gap-2">
                        {navGroups.find(g => g.id === 'platform').items.map((item) => (
                          <a
                            key={item.name}
                            href={item.href}
                            onClick={(e) => handleNavClick(e, item.href, item.onClick)}
                            className="flex items-start gap-3 p-2.5 rounded-xl border border-transparent hover:border-white/60 hover:bg-white/55 hover:shadow-sm transition-all duration-300 group cursor-pointer"
                          >
                            <div className="w-9 h-9 rounded-lg bg-indigo-50/50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors shrink-0">
                              <SafeIcon icon={item.icon} className="w-4.5 h-4.5" />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{item.name}</div>
                              <div className="text-xs text-slate-500 line-clamp-1">{item.desc}</div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>

                    {/* Column 2: Event Creators */}
                    <div className="space-y-4 col-span-1">
                      <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-widest border-b border-indigo-50 pb-2">
                        {t('navbar.eventCreators.title')}
                      </h3>
                      <div className="space-y-1">
                        {[
                          { name: t('navbar.eventCreators.firstTimers'), desc: t('navbar.eventCreators.firstTimersDesc'), href: 'https://app.bookmeinscotty.com/add-item', icon: FiCompass },
                          { name: t('navbar.eventCreators.eventPros'), desc: t('navbar.eventCreators.eventProsDesc'), href: `/${currentLang}?page=solutions&tab=pilot`, icon: FiAward },
                          { name: t('navbar.eventCreators.freeEvents'), desc: t('navbar.eventCreators.freeEventsDesc'), href: getHref('#pricing'), icon: FiPercent },
                          { name: t('navbar.eventCreators.developers'), desc: t('navbar.eventCreators.developersDesc'), href: 'https://docs.bookmein.events/', icon: FiCode },
                        ].map((item) => (
                          <a
                            key={item.name}
                            href={item.href}
                            onClick={(e) => handleNavClick(e, item.href)}
                            className="flex items-start gap-3 p-2.5 rounded-xl border border-transparent hover:border-white/60 hover:bg-white/55 hover:shadow-sm transition-all duration-300 group cursor-pointer"
                          >
                            <div className="w-9 h-9 rounded-lg bg-indigo-50/50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors shrink-0">
                              <SafeIcon icon={item.icon} className="w-4.5 h-4.5" />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{item.name}</div>
                              <div className="text-xs text-slate-500 line-clamp-1">{item.desc}</div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>

                    {/* Column 3: Industry solutions */}
                    <div className="space-y-4 col-span-1">
                      <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-widest border-b border-indigo-50 pb-2">
                        {t('navbar.industrySolutions.title')}
                      </h3>
                      <div className="space-y-1">
                        {[
                          { name: t('navbar.industrySolutions.festivals'), desc: t('navbar.industrySolutions.festivalsDesc'), href: `/${currentLang}?page=solutions&tab=pilot`, icon: FiMusic },
                          { name: t('navbar.industrySolutions.workshops'), desc: t('navbar.industrySolutions.workshopsDesc'), href: `/${currentLang}?page=solutions&tab=unity`, icon: FiBookOpen },
                          { name: t('navbar.industrySolutions.conferences'), desc: t('navbar.industrySolutions.conferencesDesc'), href: `/${currentLang}?page=solutions&tab=pollio`, icon: FiUsers },
                          { name: t('navbar.industrySolutions.agritourism'), desc: t('navbar.industrySolutions.agritourismDesc'), href: getHref('#formats'), icon: FiFeather },
                          { name: t('navbar.industrySolutions.charities'), desc: t('navbar.industrySolutions.charitiesDesc'), href: getHref('#pricing'), icon: FiHeart },
                        ].map((item) => (
                          <a
                            key={item.name}
                            href={item.href}
                            onClick={(e) => handleNavClick(e, item.href)}
                            className="flex items-start gap-3 p-2.5 rounded-xl border border-transparent hover:border-white/60 hover:bg-white/55 hover:shadow-sm transition-all duration-300 group cursor-pointer"
                          >
                            <div className="w-9 h-9 rounded-lg bg-indigo-50/50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors shrink-0">
                              <SafeIcon icon={item.icon} className="w-4.5 h-4.5" />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{item.name}</div>
                              <div className="text-xs text-slate-500 line-clamp-1">{item.desc}</div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Solutions Mega Menu with 9 Specialized Solution Tabs & Lifecycle Overview Card */}
                {activeMega === 'solutions' && (
                  <>
                    <div className="space-y-4 col-span-3">
                      <div className="flex items-center justify-between border-b border-indigo-50 pb-2">
                        <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-widest">
                          {t('navbar.groups.solutions')} — Event Management Suite
                        </h3>
                        <span className="text-[11px] font-semibold text-slate-400">
                          9 Specialized Engines
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {navGroups.find(g => g.id === 'solutions')?.items.map((item) => (
                          <a
                            key={item.name}
                            href={item.href}
                            onClick={(e) => handleNavClick(e, item.href)}
                            className="flex items-start gap-3 p-3 rounded-xl border border-transparent hover:border-white/60 hover:bg-white/55 hover:shadow-sm transition-all duration-300 group cursor-pointer"
                          >
                            <div className="w-9 h-9 rounded-lg bg-indigo-50/50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors shrink-0">
                              <SafeIcon icon={item.icon} className="w-4.5 h-4.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                                  {item.name}
                                </span>
                                {item.isNew && (
                                  <span className="text-[9px] font-black uppercase tracking-wider text-white bg-amber-500 px-1.5 py-0.2 rounded-md shrink-0">
                                    NEW
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">{item.desc}</div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>

                    {/* Solutions Promo Showcase Card */}
                    <div className="bg-gradient-to-tr from-indigo-900 via-indigo-950 to-slate-950 rounded-2xl p-6 text-white border border-white/10 relative overflow-hidden shadow-inner flex flex-col justify-between group/card text-left col-span-1">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>
                      <div className="relative z-10">
                        <span className="text-[9px] font-black uppercase tracking-widest text-indigo-300 bg-indigo-800/40 px-2 py-0.5 rounded border border-indigo-700/50">
                          Lifecycle Suite
                        </span>
                        <h4 className="text-lg font-black mt-3 leading-tight">
                          The Event Lifecycle of Success
                        </h4>
                        <p className="text-[11px] text-indigo-200 mt-2 leading-relaxed">
                          From planning & orchestration to immersion, AI digital humans, and multi-network broadcasting.
                        </p>
                      </div>
                      <a 
                        href={`/${currentLang}?page=solutions&tab=ecosystem`}
                        onClick={(e) => handleNavClick(e, `/${currentLang}?page=solutions&tab=ecosystem`)}
                        className="mt-6 flex items-center gap-1.5 text-xs font-bold text-indigo-300 hover:text-white transition-colors group/link cursor-pointer"
                      >
                        Explore Full Suite
                        <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-1" />
                      </a>
                    </div>
                  </>
                )}

                {activeMega === 'resources' && (
                  <div className="space-y-4 col-span-2">
                    <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-widest border-b border-indigo-50 pb-2">
                      {t('navbar.groups.resources')}
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {navGroups.find(g => g.id === 'resources').items.map((item) => (
                        <a
                          key={item.name}
                          href={item.href}
                          onClick={(e) => handleNavClick(e, item.href, item.onClick)}
                          className="flex items-start gap-3 p-2.5 rounded-xl border border-transparent hover:border-white/60 hover:bg-white/55 hover:shadow-sm transition-all duration-300 group cursor-pointer"
                        >
                          <div className="w-9 h-9 rounded-lg bg-indigo-50/50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors shrink-0">
                            <SafeIcon icon={item.icon} className="w-4.5 h-4.5" />
                          </div>
                          <div>
                            <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{item.name}</div>
                            <div className="text-xs text-slate-500 line-clamp-1">{item.desc}</div>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {activeMega === 'integrations' && (
                  <div className="space-y-4 col-span-3">
                    <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-widest border-b border-indigo-50 pb-2">
                      {t('navbar.items.integrations')}
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      {navGroups.find(g => g.id === 'integrations').items.map((item) => (
                        <a
                          key={item.name}
                          href={item.href}
                          onClick={(e) => handleNavClick(e, item.href, item.onClick)}
                          className="flex items-start gap-3.5 p-3.5 rounded-xl border border-transparent hover:border-white/60 hover:bg-white/55 hover:shadow-sm transition-all duration-300 group cursor-pointer"
                        >
                          <div className="w-10 h-10 rounded-xl bg-indigo-50/60 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors shrink-0">
                            <SafeIcon icon={item.icon} className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{item.name}</div>
                            <div className="text-xs text-slate-500 leading-relaxed mt-1">{item.desc}</div>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {activeMega === 'resources' && (
                  <div className="bg-gradient-to-tr from-violet-900 to-indigo-950 rounded-2xl p-6 text-white border border-white/10 relative overflow-hidden shadow-inner flex flex-col justify-between group/card text-left">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-violet-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>
                    <div className="relative z-10">
                      <span className="text-[9px] font-black uppercase tracking-widest text-violet-300 bg-violet-800/40 px-2 py-0.5 rounded border border-violet-700/50">
                        {t('navbar.blogPromo.badge')}
                      </span>
                      <h4 className="text-lg font-black mt-3 leading-tight">
                        {t('navbar.blogPromo.title')}
                      </h4>
                      <p className="text-[11px] text-violet-200 mt-2 leading-relaxed">
                        {t('navbar.blogPromo.desc')}
                      </p>
                    </div>
                    <a 
                      href="https://blog.bookmein.events"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setActiveMega(null)}
                      className="mt-6 flex items-center gap-1.5 text-xs font-bold text-violet-300 hover:text-white transition-colors group/link cursor-pointer"
                    >
                      {t('navbar.blogPromo.cta')}
                      <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-1" />
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Bottom-Sheet Mobile Menu Overlay - Independent of top header */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed inset-x-0 top-16 bottom-[58px] bg-white rounded-t-3xl border-t border-slate-200 shadow-2xl z-[99998] md:hidden overflow-hidden flex flex-col"
          >
            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
                <span className="text-xs font-extrabold uppercase tracking-widest text-indigo-600">Navigation Menu</span>
                <button 
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-1.5 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-1 mb-6">
                {navGroups.map((group) => (
                  group.items && group.items.length > 0 ? (
                    <MobileAccordion 
                      key={group.id} 
                      group={group} 
                      setMobileMenuOpen={setMobileMenuOpen} 
                      handleNavClick={handleNavClick}
                      currentLang={currentLang}
                      getHref={getHref}
                    />
                  ) : (
                    <a
                      key={group.id}
                      href={group.href || '#'}
                      onClick={(e) => handleNavClick(e, group.href)}
                      className="block py-4 text-slate-900 font-bold border-b border-slate-50 cursor-pointer"
                    >
                      {group.label}
                    </a>
                  )
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky Mobile Bottom Navigation Bar - Independent of top header */}
      <div className="fixed bottom-0 left-0 right-0 z-[99999] md:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200/80 px-3 py-2 shadow-2xl flex items-center justify-between gap-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <a 
          href="https://app.bookmeinscotty.com/"
          onClick={() => trackEvent('cta_click', { cta: 'login', section: 'navbar_mobile_sticky' })}
          className="flex-1 py-2 px-3 bg-gradient-primary text-white rounded-full text-xs font-bold text-center active:scale-95 transition-all shadow-md shadow-indigo-500/20 truncate flex items-center justify-center"
        >
          {t('navbar.login')}
        </a>

        <LanguageSelector dropUp={true} />

        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-xs font-bold transition-all shadow-sm ${
            mobileMenuOpen ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-100 border-slate-200 text-slate-700'
          }`}
        >
          <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="w-4 h-4" />
          <span>Menu</span>
        </button>
      </div>
    </>
  );
};

export default Navbar;